import axios from "axios";
import { OrderDTOMapper } from "../types/dto.types";
import TokenManagerHelper from "./token_manager_helper";

// Configuração da URL base da API para Orders
const getOrderApiBaseURL = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (process.env.NODE_ENV === "development") {
    return "http://localhost:3000";
  }

  return "https://appdav-qa.coffcode.com";
};

const API_BASE_URL = getOrderApiBaseURL();

// Interfaces baseadas no DTO do contrato
interface OrderItem {
  documentOwner: string;
  idDav: number;
  idProduct: number;
  id: number;
  quantity: number;
  unitPrice: number;
  promotionPrice: number;
  promotionProrated: number;
  observation: string;
}

interface Order {
  documentOwner: string;
  id: number;
  sellerId: number;
  customerId: number;
  dateEmission: string;
  status: number;
  amountProducts: number;
  amountPromotion: number;
  observation: string;
  paymentMethodId: number;
  paymentFormId: number;
  items: OrderItem[];
  customerName?: string;
  paymentMethodName?: string;
  paymentFormName?: string;
  sellerName?: string;
  localCreatedAt: string;
}

class RealOrderService {
  private readonly baseURL: string;
  private isSyncing = false;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Buscar todos os pedidos (localStorage por enquanto)
   */
  async getOrders(): Promise<Order[]> {
    try {
      const ordersData = localStorage.getItem("localOrders");
      const orders = ordersData ? JSON.parse(ordersData) : [];

      // Enriquecer dados com informações de customers, paymentMethods, etc.
      const enrichedOrders = await this.enrichOrdersData(orders);

      return enrichedOrders;
    } catch (error) {
      console.error("❌ Erro ao buscar pedidos:", error);
      throw new Error("Erro ao buscar pedidos do localStorage");
    }
  }

  /**
   * Adicionar novo pedido (localStorage por enquanto)
   */
  async addOrder(orderData: Omit<Order, "id"> | Order): Promise<Order> {
    try {
      const existingId = "id" in orderData ? (orderData as Order).id : undefined;
      const newId = existingId || Date.now() * 1000 + Math.floor(Math.random() * 1000);
      const now = new Date().toISOString();
      let newOrder: Order = {
        ...orderData,
        id: newId,
        localCreatedAt: now,
      };

      // Enriquecer os dados do novo pedido com nomes
      const enrichedOrders = await this.enrichOrdersData([newOrder]);
      newOrder = enrichedOrders[0];

      // Buscar pedidos existentes (sem enriquecer para salvar apenas dados básicos)
      const existingOrdersData = localStorage.getItem("localOrders");
      const existingOrders = existingOrdersData ? JSON.parse(existingOrdersData) : [];

      // Adicionar apenas dados básicos ao localStorage (sem customerName, etc.)
      const basicOrderData = {
        documentOwner: newOrder.documentOwner,
        id: newOrder.id,
        sellerId: newOrder.sellerId,
        customerId: newOrder.customerId,
        dateEmission: newOrder.dateEmission,
        status: newOrder.status,
        amountProducts: newOrder.amountProducts,
        amountPromotion: newOrder.amountPromotion,
        observation: newOrder.observation,
        paymentMethodId: newOrder.paymentMethodId,
        paymentFormId: newOrder.paymentFormId,
        items: newOrder.items,
        // removido campo de sincronização
        localCreatedAt: newOrder.localCreatedAt,
      };

      const updatedOrders = [...existingOrders, basicOrderData];

      // Salvar no localStorage
      localStorage.setItem("localOrders", JSON.stringify(updatedOrders));

      // Retornar o pedido enriquecido com nomes
      return newOrder;
    } catch (error) {
      console.error("❌ Erro ao adicionar pedido:", error);
      throw new Error("Erro ao adicionar pedido");
    }
  }

  /**
   * Atualizar pedido (localStorage por enquanto)
   */
  async updateOrder(orderData: Order): Promise<Order> {
    try {
      const existingOrdersData = localStorage.getItem("localOrders");
      const existingOrders: Order[] = existingOrdersData ? JSON.parse(existingOrdersData) : [];

      const basicOrderData = {
        documentOwner: orderData.documentOwner,
        id: orderData.id,
        sellerId: orderData.sellerId,
        customerId: orderData.customerId,
        dateEmission: orderData.dateEmission,
        status: orderData.status,
        amountProducts: orderData.amountProducts,
        amountPromotion: orderData.amountPromotion,
        observation: orderData.observation,
        paymentMethodId: orderData.paymentMethodId,
        paymentFormId: orderData.paymentFormId,
        items: orderData.items,
      };

      const updatedOrders = existingOrders.map((order) =>
        order.id === orderData.id ? basicOrderData : order
      );

      localStorage.setItem("localOrders", JSON.stringify(updatedOrders));

      const enrichedOrders = await this.enrichOrdersData([orderData]);
      return enrichedOrders[0];
    } catch (error) {
      console.error("❌ Erro ao atualizar pedido:", error);
      throw new Error("Erro ao atualizar pedido");
    }
  }

  /**
   * Deletar pedido (localStorage por enquanto)
   */
  async deleteOrder(orderId: number): Promise<string> {
    try {
      const ordersData = localStorage.getItem("localOrders");
      const allOrders: Order[] = ordersData ? JSON.parse(ordersData) : [];

      const order = allOrders.find((o) => o.id === orderId);
      if (order && order.status === 2) {
        throw new Error("Pedidos sincronizados não podem ser excluídos");
      }

      const filteredOrders = allOrders.filter((o) => o.id !== orderId);
      localStorage.setItem("localOrders", JSON.stringify(filteredOrders));

      return "Pedido deletado com sucesso";
    } catch (error) {
      console.error("❌ Erro ao deletar pedido:", error);
      throw error instanceof Error ? error : new Error("Erro ao deletar pedido");
    }
  }

  /**
   * Enriquecer dados dos pedidos com informações de customers, paymentMethods, etc.
   */
  private async enrichOrdersData(orders: Order[]): Promise<Order[]> {
    try {
      // Buscar dados do localStorage/DAV - primeiro tenta davData, depois individual
      let customers: any[] = [];
      let paymentMethods: any[] = [];
      let paymentForms: any[] = [];

      // Tentar buscar do davData primeiro
      const davData = localStorage.getItem("davData");
      if (davData) {
        const parsedDavData = JSON.parse(davData);
        customers = parsedDavData.customers || [];
        paymentMethods = parsedDavData.paymentMethods || [];
        paymentForms = parsedDavData.paymentForms || [];
      }

      // Fallback para dados individuais se davData não estiver disponível
      if (customers.length === 0) {
        const customersData = localStorage.getItem("customers");
        customers = customersData ? JSON.parse(customersData) : [];
      }

      if (paymentMethods.length === 0) {
        const paymentMethodsData = localStorage.getItem("paymentMethods");
        paymentMethods = paymentMethodsData ? JSON.parse(paymentMethodsData) : [];
      }

      if (paymentForms.length === 0) {
        const paymentFormsData = localStorage.getItem("paymentForms");
        paymentForms = paymentFormsData ? JSON.parse(paymentFormsData) : [];
      }

      return orders.map((order) => {
        const customer = customers.find(
          (c: { id: number; name: string }) => c.id === order.customerId
        );
        const paymentMethod = paymentMethods.find(
          (pm: { id: number; description: string }) => pm.id === order.paymentMethodId
        );
        const paymentForm = paymentForms.find(
          (pf: { id: number; description: string }) => pf.id === order.paymentFormId
        );

        return {
          ...order,
          customerName: customer?.name || order.customerId?.toString(),
          paymentMethodName: paymentMethod?.description || order.paymentMethodId?.toString(),
          paymentFormName: paymentForm?.description || order.paymentFormId?.toString(),
        };
      });
    } catch (error) {
      console.error("❌ Erro ao enriquecer dados dos pedidos:", error);
      return orders;
    }
  }

  async syncPendingOrders(): Promise<{ syncedCount: number }> {
    if (this.isSyncing) {
      return { syncedCount: 0 };
    }

    this.isSyncing = true;
    try {
      const ordersData = localStorage.getItem("localOrders");
      const allOrders: Order[] = ordersData ? JSON.parse(ordersData) : [];
      const pendingOrders = allOrders.filter((order) => order.status === 1);

      if (pendingOrders.length === 0) {
        return { syncedCount: 0 };
      }

      const token = TokenManagerHelper.getBearerToken();
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }

      const userDataStr = localStorage.getItem("authUser");
      if (!userDataStr) {
        throw new Error("Dados do usuário não encontrados");
      }

      const userData = JSON.parse(userDataStr);
      const documentOwner = userData.documentOwner;
      if (!documentOwner) {
        throw new Error("Documento do proprietário não encontrado");
      }

      const ordersDTO = pendingOrders.map((order) =>
        OrderDTOMapper.toReceiveDataFromDavMobile(order)
      );

      await axios.post(
        `${this.baseURL}/api/v1/receives-data/from-dav-mobile/${documentOwner}`,
        ordersDTO,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token,
          },
        }
      );

      const freshData = localStorage.getItem("localOrders");
      const freshOrders: Order[] = freshData ? JSON.parse(freshData) : [];
      const syncedIds = new Set(pendingOrders.map((o) => o.id));
      const updatedOrders = freshOrders.map((order) =>
        syncedIds.has(order.id) ? { ...order, status: 2 } : order
      );
      localStorage.setItem("localOrders", JSON.stringify(updatedOrders));

      return { syncedCount: pendingOrders.length };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Buscar pedidos não sincronizados
   */
  async getPendingOrders(): Promise<Order[]> {
    try {
      const allOrders = await this.getOrders();
      return allOrders.filter((order) => order.status !== 2);
    } catch (error) {
      console.error("❌ Erro ao buscar pedidos pendentes:", error);
      throw new Error("Erro ao buscar pedidos pendentes");
    }
  }

  /**
   * Marcar pedido como sincronizado
   */
  async markAsSynced(orderId: number): Promise<Order> {
    try {
      const existingOrdersData = localStorage.getItem("localOrders");
      const existingOrders = existingOrdersData ? JSON.parse(existingOrdersData) : [];

      const updatedOrders = existingOrders.map((order: any) =>
        order.id === orderId ? { ...order, status: 2 } : order
      );

      localStorage.setItem("localOrders", JSON.stringify(updatedOrders));

      const updatedOrder = updatedOrders.find((order: any) => order.id === orderId);
      if (!updatedOrder) {
        throw new Error("Pedido não encontrado após marcação");
      }

      const enrichedOrders = await this.enrichOrdersData([updatedOrder]);
      return enrichedOrders[0];
    } catch (error) {
      console.error("❌ Erro ao marcar pedido como sincronizado:", error);
      throw new Error("Erro ao marcar pedido como sincronizado");
    }
  }

  /**
   * Marcar pedido com erro de sincronização
   */
  async markAsSyncError(orderId: number, errorMessage: string): Promise<Order> {
    try {
      const existingOrdersData = localStorage.getItem("localOrders");
      const existingOrders = existingOrdersData ? JSON.parse(existingOrdersData) : [];

      const updatedOrders = existingOrders.map((order: any) =>
        order.id === orderId ? { ...order, status: 1 } : order
      );

      localStorage.setItem("localOrders", JSON.stringify(updatedOrders));

      const updatedOrder = updatedOrders.find((order: any) => order.id === orderId);
      if (!updatedOrder) {
        throw new Error("Pedido não encontrado após marcação de erro");
      }

      const enrichedOrders = await this.enrichOrdersData([updatedOrder]);
      return enrichedOrders[0];
    } catch (error) {
      console.error("❌ Erro ao marcar erro de sincronização:", error);
      throw new Error("Erro ao marcar erro de sincronização");
    }
  }
}

// Instância singleton
export const realOrderService = new RealOrderService();

export default realOrderService;
